import React, { useState } from "react";
import { Minus, Plus } from "lucide-react";

const LINK = "font-semibold text-[#017bef] hover:underline";

// Copy from "SPACIA Website Content - Final I", section 6 (FAQs)
const FAQS: { question: string; answer: React.ReactNode }[] = [
  {
    question: "What is SPACIA?",
    answer:
      "SPACIA is a platform that connects people to flexible workspaces, quality accommodation and curated lifestyle experiences organised around three pillars: Work, Stay and Play.",
  },
  {
    question: "Who is SPACIA for?",
    answer:
      "SPACIA is built for professionals who need flexible workspace, travellers who need somewhere to Work or stay on the move, and teams who need space for offsites, retreats, or client meetings.",
  },
  {
    question: "How do I sign up?",
    answer: "Create a profile on the SPACIA app or website, then start booking right away.",
  },
  {
    question: "What can I book on SPACIA?",
    answer:
      "Under Work, you can book coworking spaces, dedicated desks, meeting rooms, conference rooms and private offices. Under Stay, you can book hotels, serviced apartments and boutique hotels. Under Play, you can book wellness services, dining reservations, adventure experiences, event spaces and Concierge support.",
  },
  {
    question: "Is SPACIA available in my city?",
    answer: "SPACIA is currently available in Kenya and Ghana and expanding across new cities and locations.",
  },
  {
    question: "Are the spaces and venues on SPACIA verified?",
    answer:
      "Yes. Every workspace, property and provider is verified before appearing on the platform, so users can book with confidence.",
  },
  {
    question: "Can I pay online?",
    answer: "Yes. SPACIA supports secure online payments for every booking, across Work, Stay and Play.",
  },
  {
    question: "Can I cancel or amend a booking?",
    answer:
      "Yes. Cancellation and amendment options vary by space and provider and are clearly displayed before you book.",
  },
  {
    question: "How do I list my space or venue on SPACIA?",
    answer:
      "Register as a partner, add your space or venue, and set your availability and pricing. Our team will guide you through verification and onboarding.",
  },
  {
    question: "Is my data secure?",
    answer:
      "Yes. SPACIA uses enterprise-grade security, encryption and role-based access to protect member and partner data.",
  },
  {
    question: "Can businesses manage bookings for their whole team?",
    answer:
      "Yes. Business Plans allow companies to manage Work and Stay bookings for their teams from one account, with centralised billing and reporting.",
  },
  {
    question: "How do I book a space?",
    answer:
      "Log into the app or website and browse available locations on the Explore page, filter by Work, Stay, or Play, and reserve and pay instantly.",
  },
  {
    question: "Can I book on behalf of my team?",
    answer: "Yes. Teams can book meeting rooms, offsites, and corporate travel packages through SPACIA.",
  },
  {
    question: "What do I earn by booking through SPACIA?",
    // Softened: the rewards programme has not launched yet
    answer:
      "A SPACIA rewards programme is on the way. Members will earn points on stays booked through SPACIA that can be redeemed for activities and experiences — we'll announce the details when it launches.",
  },
  {
    question: "Does my membership work across all SPACIA locations?",
    answer:
      "Yes, a single SPACIA membership gives you access to the full network of partner locations, rather than being tied to one space.",
  },
  {
    question: "Who do I contact if I have an issue?",
    answer: (
      <>
        Contact SPACIA's support directly through the app, or reach the team via email at{" "}
        <a href="mailto:sales@spacia.africa" className={LINK}>
          sales@spacia.africa
        </a>{" "}
        or call{" "}
        <a href="tel:+233546911835" className={LINK}>
          +233 546 911835
        </a>{" "}
        or{" "}
        <a href="tel:+254732188105" className={LINK}>
          +254 732 188 105
        </a>{" "}
        or visit ICEA Lion Riverside Park, West Wing – 2nd Floor.
      </>
    ),
  },
];

export default function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-[768px] px-[16px] sm:px-8">
        <p className="text-center text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">FAQ</p>
        <h2 className="pt-[16px] text-center text-[30px] font-bold leading-10 tracking-[-0.9px] text-[#0a0a0a] sm:text-[36px]">
          Learn More About SPACIA
        </h2>

        <div className="mt-10 rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-white px-6 shadow-[0_1px_1.5px_rgba(0,0,0,0.1),0_1px_1px_rgba(0,0,0,0.1)] sm:px-8">
          {FAQS.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div key={faq.question} className="border-b-[1px] border-solid border-[#e5e7eb] last:border-b-0">
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-[16px] py-6 text-left"
                >
                  <span className="text-[16px] font-semibold leading-6 text-[#1d293d]">{faq.question}</span>
                  <span
                    className={`flex size-7 shrink-0 items-center justify-center rounded-full border-[1px] border-solid ${
                      isOpen
                        ? "border-[#017bef]/30 bg-[#017bef]/[0.06] text-[#017bef]"
                        : "border-black/10 text-[#717182]"
                    }`}
                  >
                    {isOpen ? <Minus className="size-3.5" /> : <Plus className="size-3.5" />}
                  </span>
                </button>
                {isOpen && <p className="pb-6 text-[14px] leading-[22.75px] text-[#717182]">{faq.answer}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
